import { addDays, dayOfWeek, type IsoDate } from "@/lib/time/dates";

/**
 * My Team numbers on one base (DECISIONS 2026-10-15): available = capacity - PTO, and
 * utilization / billable % are measured to date. Pure; the browser and the CSV both use it.
 */

/** Below target = billable % under 90% of the person's target. */
const BELOW_TARGET_SHARE = 0.9;
/** Under-planned = planned under 75% of available. */
const UNDER_PLANNED_SHARE = 0.75;

export interface TeamMember {
  personId: string;
  personName: string;
  scheduleType: string | null;
  isFullTime: boolean;
}

/** One report, one month, as `ed-team-stats` returns it (hours unless named otherwise). */
export interface MonthStatsRow {
  personId: string;
  personName: string;
  monthStart: IsoDate;
  monthEnd: IsoDate;
  capacity: number | null;
  targetPct: number | null;
  ptoMonth: number;
  ptoToDate: number;
  loggedToDate: number;
  billableToDate: number;
  billableAmountToDate: number;
  planned: number;
  plannedBillable: number;
}

/** The twelve Details numbers; the same keys on a person and on the team totals. */
export interface MetricValues {
  capacity: number | null;
  pto: number;
  available: number | null;
  planned: number;
  plannedBillable: number;
  plannedPct: number | null;
  targetHours: number | null;
  targetPct: number | null;
  billable: number;
  billablePct: number | null;
  logged: number;
  utilization: number | null;
}

export interface PersonMetrics extends MetricValues {
  personId: string;
  personName: string;
  monthStart: IsoDate;
  availableToDate: number | null;
  billableAmount: number;
  /** At least one workday of the month is over, so logged numbers mean something. */
  started: boolean;
  belowTarget: boolean;
  underPlanned: boolean;
  /** 2 = below target, 1 = under-planned, 3 = both; higher sorts first. */
  rank: number;
}

export interface TeamTotals extends MetricValues {
  availableToDate: number;
  started: boolean;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

const ratio = (num: number, den: number | null): number | null =>
  den !== null && den > 0 ? num / den : null;

/** Monday-Friday dates from `from` to `to`, both included; 0 when `to` is before `from`. */
export function weekdaysBetween(from: IsoDate, to: IsoDate): number {
  let count = 0;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    const dow = dayOfWeek(d);
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
}

/**
 * Share of the month's weekdays that are over by `asOf`: workdays from the 1st through
 * YESTERDAY, today excluded, so nobody is measured before they have logged today. Callers pass
 * `asOf = todayInZone(now, TIME_ZONE)`. Before the month it is 0; once `asOf` is past the
 * month's last day it is 1; on the 1st (nothing elapsed) it is 0, so to-date numbers stay blank.
 */
export function elapsedShare(monthStart: IsoDate, monthEnd: IsoDate, asOf: IsoDate): number {
  if (asOf < monthStart) return 0;
  if (asOf > monthEnd) return 1;
  const total = weekdaysBetween(monthStart, monthEnd);
  return total === 0 ? 0 : weekdaysBetween(monthStart, addDays(asOf, -1)) / total;
}

export function personMetrics(row: MonthStatsRow, asOf: IsoDate): PersonMetrics {
  const share = elapsedShare(row.monthStart, row.monthEnd, asOf);
  const started = share > 0;
  const capacity = row.capacity;
  const available = capacity === null ? null : Math.max(0, round2(capacity - row.ptoMonth));
  const availableToDate =
    capacity === null ? null : Math.max(0, round2(capacity * share - row.ptoToDate));
  const target = row.targetPct ?? 0;
  const utilization = started ? ratio(row.loggedToDate, availableToDate) : null;
  const billablePct = started ? ratio(row.billableToDate, availableToDate) : null;
  const belowTarget =
    billablePct !== null && target > 0 && billablePct < BELOW_TARGET_SHARE * target;
  const underPlanned =
    available !== null && available > 0 && row.planned < UNDER_PLANNED_SHARE * available;
  return {
    personId: row.personId,
    personName: row.personName,
    monthStart: row.monthStart,
    capacity,
    pto: row.ptoMonth,
    available,
    availableToDate,
    planned: row.planned,
    plannedBillable: row.plannedBillable,
    plannedPct: ratio(row.planned, available),
    targetHours:
      available !== null && row.targetPct !== null ? round2(available * row.targetPct) : null,
    targetPct: row.targetPct,
    billable: row.billableToDate,
    billablePct,
    logged: row.loggedToDate,
    utilization,
    billableAmount: row.billableAmountToDate,
    started,
    belowTarget,
    underPlanned,
    rank: (belowTarget ? 2 : 0) + (underPlanned ? 1 : 0),
  };
}

/** Sums over people with a capacity row, then sums divided by sums. */
export function teamTotals(people: PersonMetrics[]): TeamTotals {
  const counted = people.filter((p) => p.capacity !== null);
  const sum = (pick: (p: PersonMetrics) => number | null) =>
    round2(counted.reduce((acc, p) => acc + (pick(p) ?? 0), 0));
  const has = counted.length > 0;
  const available = has ? sum((p) => p.available) : null;
  const availableToDate = sum((p) => p.availableToDate);
  const started = counted.some((p) => p.started);
  const targetHours = has ? sum((p) => p.targetHours) : null;
  const targeted = counted.filter((p) => p.targetPct !== null);
  const targetedAvailable = round2(targeted.reduce((acc, p) => acc + (p.available ?? 0), 0));
  const logged = sum((p) => p.logged);
  const billable = sum((p) => p.billable);
  const planned = sum((p) => p.planned);
  return {
    capacity: has ? sum((p) => p.capacity) : null,
    pto: sum((p) => p.pto),
    available,
    availableToDate,
    planned,
    plannedBillable: sum((p) => p.plannedBillable),
    plannedPct: ratio(planned, available),
    targetHours,
    targetPct: targetHours === null ? null : ratio(targetHours, targetedAvailable),
    billable,
    billablePct: started ? ratio(billable, availableToDate) : null,
    logged,
    utilization: started ? ratio(logged, availableToDate) : null,
    started,
  };
}

/** Tagged rows first (below target before under-planned), then by name. */
export function sortByAttention(people: PersonMetrics[]): PersonMetrics[] {
  return [...people].sort((a, b) => b.rank - a.rank || a.personName.localeCompare(b.personName));
}

export function formatPct(value: number | null): string {
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

export function formatHours(value: number | null): string {
  if (value === null) return "—";
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

export function formatDays(days: number): string {
  if (days <= 0) return "—";
  return days === 1 ? "1 day" : `${formatHours(days)} days`;
}

/**
 * The team member a `?person=` deep link names. GUIDs are matched ignoring case (the link may
 * be lowercase; the server's ids are upper-case). undefined = not on this manager's team.
 */
export function findTeamMember<T extends { personId: string }>(
  people: T[],
  id: string | undefined,
): T | undefined {
  if (!id) return undefined;
  const wanted = id.toUpperCase();
  return people.find((p) => p.personId.toUpperCase() === wanted);
}
